"use client";
import { yupResolver } from "@hookform/resolvers/yup";
import type React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  type AddQuoteResult,
  BabyInsuredParty,
  type BabyQuote,
  type PolicyHolder,
  ProductType,
  type WebShopPublicUser,
} from "@/api_client";
import { ComposedDatePicker } from "@/components/shared/composedDatePicker/ComposedDatePicker";
import {
  ComposedRadioButton,
  ComposedRadioButtonValue,
} from "@/components/shared/composedRadioButton/ComposedRadioButton";
import { ProductContainer } from "@/components/shared/productContainer/ProductContainer";
import { SsnInput } from "@/components/shared/ssnInput/SsnInput";
import { useEntry } from "@/hooks/useEntry";
import type { BabyEntryContent } from "@/services/cms/contentTypes/entry/BabyEntryContent";
import { useEntryContent } from "@/services/cms/tanstackQuery/hooks/useEntryContent";
import { BabyEntryValidationSchema } from "@/validators/products/baby/BabyEntryValidator";

export type EntryProps = {
  externalUser?: WebShopPublicUser;
  externalPolicyHolder?: PolicyHolder | null;
  externalContent?: BabyEntryContent;
  externalIsLoading?: boolean;
  externalSubmitHandler?: (quote: BabyQuote) => Promise<AddQuoteResult>;
};

export const BabyEntryForm = ({
  externalUser,
  externalSubmitHandler,
  externalIsLoading,
  externalContent,
  externalPolicyHolder,
}: EntryProps): React.JSX.Element => {
  const { currentUser, currentPolicyHolder, isLoading, submitHandler } =
    useEntry<BabyQuote, BabyEntryContent>({
      externalUser,
      externalSubmitHandler,
      externalIsLoading,
      externalContent,
      externalPolicyHolder,
    });
  const { data: babyEntryPage } =
    useEntryContent<typeof ProductType.BABY>(externalContent);

  const insuredPartyValues =
    babyEntryPage?.insuredParty?.RadionButtonValues ?? [];
  const policyHolderOption = insuredPartyValues.find(
    (item) => item?.Value === BabyInsuredParty.POLICY_HOLDER,
  );
  const motherOption = insuredPartyValues.find(
    (item) => item?.Value === BabyInsuredParty.MOTHER,
  );

  const defaultValues: BabyQuote = {
    insuredParty: BabyInsuredParty.POLICY_HOLDER,
    productType: ProductType.BABY,
    policyHolderSsn:
      currentPolicyHolder?.prefilledSsn ?? currentUser?.ssn ?? "",
  };
  const { formState, control, handleSubmit, watch } = useForm<BabyQuote>({
    defaultValues,
    resolver: yupResolver(BabyEntryValidationSchema(babyEntryPage)),
    disabled: isLoading,
  });

  const errors = formState.errors;
  const insuredParty = watch("insuredParty");

  const isMother = insuredParty === BabyInsuredParty.POLICY_HOLDER;

  return (
    <ProductContainer>
      <ProductContainer.Entry>
        <ProductContainer.Form
          onSubmit={handleSubmit(submitHandler)}
          noValidate
        >
          <Controller
            name={"insuredParty"}
            control={control}
            render={({ field }) => (
              <ComposedRadioButton
                className="if flex-column"
                id={field.name}
                aria-describedby="insured-party-description"
                errorMessage={errors.insuredParty?.message}
                invalid={undefined}
                label={babyEntryPage?.insuredParty?.Label}
                disabled={isLoading}
                {...field}
                value={field?.value ?? ""}
                popoverMessage={babyEntryPage?.insuredParty?.Popover}
              >
                <ComposedRadioButtonValue
                  defaultChecked={
                    field.value === BabyInsuredParty.POLICY_HOLDER
                  }
                  name={field.name}
                  data-testid="policy-holder-policy-holder-radio"
                  id={`${field.name}-${BabyInsuredParty.POLICY_HOLDER.toLowerCase()}`}
                  label={policyHolderOption?.Label}
                  value={BabyInsuredParty.POLICY_HOLDER}
                  disabled={isLoading}
                />
                <ComposedRadioButtonValue
                  defaultChecked={field.value === BabyInsuredParty.MOTHER}
                  name={field.name}
                  data-testid="insured-party-mother-radio"
                  id={`${field.name}-${BabyInsuredParty.MOTHER.toLowerCase()}`}
                  label={motherOption?.Label}
                  value={BabyInsuredParty.MOTHER}
                  disabled={isLoading}
                />
              </ComposedRadioButton>
            )}
          />

          <Controller
            name={"policyHolderSsn"}
            control={control}
            render={({ field }) => (
              <SsnInput
                id={field.name}
                placeholder={
                  babyEntryPage?.policyHolderSsn?.Placeholder ?? undefined
                }
                data-testid="policy-holder-ssn-input"
                errorMessage={errors.policyHolderSsn?.message}
                disabled={
                  isLoading ||
                  currentPolicyHolder?.ssnLocked ||
                  currentUser?.isAuthenticated
                }
                popoverMessage={
                  babyEntryPage?.policyHolderSsn?.Popover ?? undefined
                }
                {...field}
              >
                {babyEntryPage?.policyHolderSsn?.Label}
              </SsnInput>
            )}
          />

          {!isMother && (
            <Controller
              name={"babyMother.ssn"}
              control={control}
              render={({ field }) => (
                <SsnInput
                  id={field.name}
                  data-testid="baby-mother-ssn-input"
                  placeholder={
                    babyEntryPage?.babyMotherSsn?.Placeholder ?? undefined
                  }
                  errorMessage={errors.babyMother?.ssn?.message}
                  disabled={isLoading || isMother}
                  popoverMessage={
                    babyEntryPage?.babyMotherSsn?.Popover ?? undefined
                  }
                  {...field}
                >
                  {babyEntryPage?.babyMotherSsn?.Label}
                </SsnInput>
              )}
            />
          )}

          <Controller
            name={"expectedDateOfBirth"}
            control={control}
            render={({ field }) => (
              <ComposedDatePicker
                id={field.name}
                placeholder={
                  babyEntryPage?.expectedDateOfBirth?.Placeholder ?? undefined
                }
                data-testid="baby-expected-date-of-birth-date-picker"
                errorMessage={errors.expectedDateOfBirth?.message}
                disabled={isLoading}
                popoverMessage={babyEntryPage?.expectedDateOfBirth?.Popover}
                {...field}
              >
                {babyEntryPage?.expectedDateOfBirth?.Label}
              </ComposedDatePicker>
            )}
          />
          <ProductContainer.CallToAction
            htmlType="submit"
            data-testid="baby-entry-submit-button"
            disabled={isLoading}
            loading={isLoading}
          >
            {babyEntryPage?.submitButton?.Label}
          </ProductContainer.CallToAction>
        </ProductContainer.Form>
      </ProductContainer.Entry>
    </ProductContainer>
  );
};
